import React, {
  useEffect,
  useMemo,
} from 'react';
import { useIsPageRoute } from '@/hooks/useIsPageRoute';
import GatorProtectAcknowledgedModal, {
  GP_MODAL_VARIANT_ACKNOWLEDGED,
  useGatorProtectAcknowledgedModal,
} from './GatorProtectAcknowledgedModal';
import GatorProtectDetectedThreadsModal, {
  GP_MODAL_VARIANT_DETECTED_THREADS,
  useGatorProtectDetectedThreadsModal,
} from './GatorProtectDetectedThreadsModal';
import GatorProtectNoThreatsModal, {
  GP_MODAL_VARIANT_NO_THREATS,
  useGatorProtectNoThreatsModal,
} from './GatorProtectNoThreatsModal';
import MigrateToGatorProtectModal, {
  GP_MODAL_VARIANT_MIGRATE_TO_GATOR_PROTECT,
  useGatorProtectMigrateModal,
} from './MigrateToGatorProtectModal';
import {
  pickGatorProtectModalToShow,
  type GPAvailableModals,
  type GPModalsVariants,
} from './gatorProtectModalsVisibilityReducer';
import type {
  GatorProtectGlobalsProps,
  GatorProtectGlobalsRoutes,
} from './GatorProtectGlobals.types';
import { useGatorProtectModalsVisibility } from './useGatorProtectModalsVisibility';
import { useGatorProtectGlobal } from './useGatorProtectGlobal';
import { recordNoThreatsCookie } from '@/pages/GatorProtect/GatorProtectHandler/utils/gatorProtectCookies';
import GatorProtectAutomatedSecurityVideo, {
  GP_MODAL_VARIANT_AUTOMATED_SECURITY_VIDEO,
  useGatorProtectAutomatedSecurityVideo,
} from './GatorProtectAutomatedSecurityVideo';

type GatorProtectGlobalsActiveModals = {
  activeModals: GPModalsVariants[];
  /** Top of stack: last entry in `activeModals` (bottom-to-top from `pickGatorProtectModalToShow`). */
  topStackModalVariant: GPModalsVariants | null;
};

function getActiveModals(
  globalsProp: GatorProtectGlobalsProps['globals'],
  acknowledgedVisibilityRule: boolean,
  detectedThreadsVisibilityRule: boolean,
  noThreatsVisibilityRule: boolean,
  migrateVisibilityRule: boolean,
  automatedSecurityVideoVisibilityRule: boolean,
  availableModals: GPAvailableModals,
): GatorProtectGlobalsActiveModals {
  const getAutomatedSecurityVideoState = (): boolean => {
    const isAutomatedSecurityVideoAvailable = availableModals[GP_MODAL_VARIANT_AUTOMATED_SECURITY_VIDEO];
    if (globalsProp?.forceAutomatedSecurityVideoVisible !== undefined) {
      return Boolean(globalsProp.forceAutomatedSecurityVideoVisible);
    }
    return automatedSecurityVideoVisibilityRule && isAutomatedSecurityVideoAvailable;
  };
  const getAcknowledgedModalState = (): boolean => {
    const isAcknowledgedModalAvailable = availableModals[GP_MODAL_VARIANT_ACKNOWLEDGED];
    if (globalsProp?.forceAcknowledgeModalVisible !== undefined) {
      return Boolean(globalsProp.forceAcknowledgeModalVisible);
    }
    return acknowledgedVisibilityRule && isAcknowledgedModalAvailable;
  };
  const getDetectedThreadsModalState = (): boolean => {
    const isDetectedThreadsModalAvailable = availableModals[GP_MODAL_VARIANT_DETECTED_THREADS];
    if (globalsProp?.forceDetectedThreadsModalVisible !== undefined) {
      return Boolean(globalsProp.forceDetectedThreadsModalVisible);
    }
    return detectedThreadsVisibilityRule && isDetectedThreadsModalAvailable;
  };
  const getNoThreatsModalState = (): boolean => {
    const isNoThreatsModalAvailable = availableModals[GP_MODAL_VARIANT_NO_THREATS];
    if (globalsProp?.forceNoThreatsModalVisible !== undefined) {
      return Boolean(globalsProp.forceNoThreatsModalVisible);
    }
    return noThreatsVisibilityRule && isNoThreatsModalAvailable;
  };
  const getMigrateModalState = (): boolean => {
    const isMigrateModalAvailable = availableModals[GP_MODAL_VARIANT_MIGRATE_TO_GATOR_PROTECT];
    if (globalsProp?.forceMigrationModalVisible !== undefined) {
      return Boolean(globalsProp.forceMigrationModalVisible);
    }
    return migrateVisibilityRule && isMigrateModalAvailable;
  };
  const modalsFinalStates: GPAvailableModals = {
    [GP_MODAL_VARIANT_AUTOMATED_SECURITY_VIDEO]: getAutomatedSecurityVideoState(),
    [GP_MODAL_VARIANT_MIGRATE_TO_GATOR_PROTECT]: getMigrateModalState(),
    [GP_MODAL_VARIANT_NO_THREATS]: getNoThreatsModalState(),
    [GP_MODAL_VARIANT_ACKNOWLEDGED]: getAcknowledgedModalState(),
    [GP_MODAL_VARIANT_DETECTED_THREADS]: getDetectedThreadsModalState(),
  };
  const activeModals = pickGatorProtectModalToShow(modalsFinalStates);
  const topStackModalVariant = activeModals.length === 0
    ? null
    : activeModals[activeModals.length - 1];
  return { activeModals, topStackModalVariant };
}

/**
 * Header Gator Protect modals: visibility reducer + modal hooks + `useGatorProtectGlobal`
 * for shared data fetches (portal modals, plans list, PRO product).
 */
export function GatorProtectGlobals({ globals: globalsProp, dependencies }: GatorProtectGlobalsProps) {
  const { summary } = dependencies;
  const {
    isHome,
    history,
    routes: routesLocale,
    isInvoicesPage,
  } = useIsPageRoute<GatorProtectGlobalsRoutes>();

  const {
    availableModals,
    hide: hideModalSlot,
    resetAll: resetModalsVisibility,
  } = useGatorProtectModalsVisibility();

  const {
    fetchModals,
    getGatorProtectPlans,
    getGatorProtectProProduct,
  } = useGatorProtectGlobal({
    summary,
    isHome,
    isInvoicesPage,
    resetModalsVisibility,
  });

  const acknowledgedModal = useGatorProtectAcknowledgedModal({
    summary,
    isValidPage: isHome,
    hideModalSlot,
  });

  const detectedThreadsModal = useGatorProtectDetectedThreadsModal({
    summary,
    isValidPage: isHome || isInvoicesPage,
    hideModalSlot,
  });

  const noThreatsModal = useGatorProtectNoThreatsModal({
    summary,
    isValidPage: isHome,
    hideModalSlot,
  });

  const migrateModal = useGatorProtectMigrateModal({
    summary,
    isValidPage: isHome,
    hideModalSlot,
  });

  const automatedSecurityVideo = useGatorProtectAutomatedSecurityVideo({
    summary,
    isHome,
    hideModalSlot,
  });

  const { activeModals, topStackModalVariant } = useMemo(
    () => getActiveModals(
      globalsProp,
      acknowledgedModal.visibilityRule,
      detectedThreadsModal.visibilityRule,
      noThreatsModal.visibilityRule,
      migrateModal.visibilityRule,
      automatedSecurityVideo.visibilityRule,
      availableModals,
    ),
    [
      globalsProp,
      acknowledgedModal.visibilityRule,
      detectedThreadsModal.visibilityRule,
      noThreatsModal.visibilityRule,
      migrateModal.visibilityRule,
      automatedSecurityVideo.visibilityRule,
      availableModals,
    ],
  );

  useEffect(() => {
    fetchModals();
  }, [fetchModals]);

  useEffect(() => {
    getGatorProtectPlans();
    getGatorProtectProProduct();
  }, [getGatorProtectPlans, getGatorProtectProProduct]);

  const onDetectedThreadsCTAHandler = (): void => {
    const { detectedThreads } = detectedThreadsModal.data;
    if (detectedThreads == null) {
      return;
    }
    history.push(`${routesLocale.gatorProtect}/${detectedThreads.hostingId}`);
  };

  const onNoThreatsModalPrimary = (): void => {
    recordNoThreatsCookie();
    history.push(routesLocale.gatorProtect);
  };
  return (
    <>
      {activeModals.includes(GP_MODAL_VARIANT_MIGRATE_TO_GATOR_PROTECT) && (
        <MigrateToGatorProtectModal
          onClose={() => migrateModal.utils.hideMigrateModal(topStackModalVariant)}
          consultedProducts={migrateModal.data.consultedProducts}
          getProPrice={getGatorProtectProProduct}
          handleRedirectToGatorProtect={migrateModal.utils.redirectMigrateModal}
        />
      )}
      {activeModals.includes(GP_MODAL_VARIANT_NO_THREATS) && (
        <GatorProtectNoThreatsModal
          onClose={() => noThreatsModal.utils.hideNoThreatsModal(topStackModalVariant)}
          onPrimary={onNoThreatsModalPrimary}
          pricingFootnoteDisplayValue={noThreatsModal.data.noThreatsModalPricing}
          isPricingFootnoteLoading={noThreatsModal.data.noThreatsModalPricingIsLoading}
        />
      )}
      {activeModals.includes(GP_MODAL_VARIANT_ACKNOWLEDGED) && (
        <GatorProtectAcknowledgedModal
          handleClose={() => acknowledgedModal.utils.hideAcknowledgedModal(topStackModalVariant)}
          handleRedirect={acknowledgedModal.utils.redirectAcknowledgedModal}
          selectedPlan={acknowledgedModal.data.selectedAckedPlan}
        />
      )}
      {activeModals.includes(GP_MODAL_VARIANT_DETECTED_THREADS) && (
        <GatorProtectDetectedThreadsModal
          threatCount={detectedThreadsModal.data.detectedThreads?.threatCount ?? 0}
          onClose={() => detectedThreadsModal.utils.hideDetectedThreadsModal(topStackModalVariant)}
          onPrimary={onDetectedThreadsCTAHandler}
        />
      )}
      {activeModals.includes(GP_MODAL_VARIANT_AUTOMATED_SECURITY_VIDEO) && (
        <GatorProtectAutomatedSecurityVideo
          onClose={() => automatedSecurityVideo.utils.hideAutomatedSecurityVideo(topStackModalVariant)}
          onPrimary={automatedSecurityVideo.utils.redirectAutomatedSecurityVideo}
          onSecondary={() => automatedSecurityVideo.utils.hideAutomatedSecurityVideo(topStackModalVariant)}
        />
      )}
    </>
  );
}
